import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Table, Checkbox } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  INPUT_TYPE,
  INPUT_COMPS,
} from '@components/dragableTableFrom/formItems';
import Actions from '@flux/action';
// import { configConsumerProps } from 'antd/lib/config-provider';

const DeleteBtn = INPUT_COMPS[INPUT_TYPE.DELETE];

const ACCESS_MAP = {
  MANAGE_WHITE_LIST: 'ServiceControl.Permission',
  NOTICE_EMAIL: 'SystemAdmin.Gateway.NoticeEmail',

  DBM: 'SystemAccess.DBM', // DBM name changed to SA
  TSA: 'SystemAccess.TSA',
  GATEWAY: 'SystemAccess.Gateway',

  DBM_ADMIN: 'SystemAdmin.DBM',
  TSA_ADMIN: 'SystemAdmin.TSA',
  GATEWAY_ADMIN: 'SystemAdmin.Gateway',
};

const ACCESS_CHECK_GROUP_OPTIONS = [
  {
    value: ACCESS_MAP.GATEWAY,
    label: 'M&A Gateway',
  },
  {
    value: ACCESS_MAP.TSA,
    label: 'TSA Accelerator',
  },
  {
    value: ACCESS_MAP.DBM,
    label: 'Standalone Accelerator',
  },
];

const ADMIN_CHECK_GROUP_OPTIONS = [
  {
    value: ACCESS_MAP.GATEWAY_ADMIN,
    label: 'M&A Gateway',
  },
  {
    value: ACCESS_MAP.TSA_ADMIN,
    label: 'TSA Accelerator',
  },
  {
    value: ACCESS_MAP.DBM_ADMIN,
    label: 'Standalone Accelerator',
  },
];

const toolLabels = ACCESS_CHECK_GROUP_OPTIONS.map((i) => (
  <div className="white-list-tool-label" key={i.value}>
    {i.label}
  </div>
));

const PermissionCheckBoxItem = ({ data = {}, checked, onChange }) => {
  const handleCheckBoxClick = (e) => {
    const newChecked = e?.target.checked;
    onChange((permission) => {
      let nextPermission = permission;

      const relatedAccessCode = data.value.replace(
        'SystemAdmin',
        'SystemAccess',
      );
      if (newChecked) {
        nextPermission = [...permission, data.value];
        if (
          data.value.includes('SystemAdmin') &&
          !permission.includes(relatedAccessCode)
        ) {
          nextPermission.push(relatedAccessCode);
        }
        if (data.value === ACCESS_MAP.GATEWAY_ADMIN) {
          nextPermission.push(ACCESS_MAP.NOTICE_EMAIL);
        }
      } else {
        if (data.value.includes('SystemAdmin')) {
          if (permission.includes(relatedAccessCode)) {
            nextPermission = nextPermission.filter(
              (i) => i !== relatedAccessCode,
            );
          }
        }
        if (data.value === ACCESS_MAP.GATEWAY_ADMIN) {
          nextPermission = nextPermission.filter(
            (i) => i !== ACCESS_MAP.NOTICE_EMAIL,
          );
        }
        nextPermission = nextPermission.filter((p) => p !== data.value);
      }

      return nextPermission;
    });
  };
  return (
    <div className="white-list-permission-checkbox">
      {/*<span>{data.label}</span>*/}
      <Checkbox checked={checked} onChange={handleCheckBoxClick} />
    </div>
  );
};

const PermissionCheckGroup = ({
  permission: oriPermission,
  userInfo = {},
  options,
}) => {
  const dispatch = useDispatch();

  const [permission, setPermisson] = useState(oriPermission);

  useEffect(() => {
    if (oriPermission !== permission) {
      dispatch(Actions.updateUserPermission({ ...userInfo, permission }));
    }
  }, [permission]);
  return options.map((l) => (
    <PermissionCheckBoxItem
      key={`permission-${l.value}`}
      data={l}
      checked={permission.includes(l.value)}
      onChange={setPermisson}
    />
  ));
};

const ServiceControlCheckBox = ({ permission, userInfo, index }) => {
  const dispatch = useDispatch();

  const onChange = useCallback(
    (e) => {
      const checked = e.target.checked;
      let nextPermission = [...permission];
      const hasServiceControlCode = nextPermission.includes(
        ACCESS_MAP.MANAGE_WHITE_LIST,
      );
      if (!checked && hasServiceControlCode) {
        nextPermission = nextPermission.filter(
          (i) => i !== ACCESS_MAP.MANAGE_WHITE_LIST,
        );
      } else if (checked && !hasServiceControlCode) {
        nextPermission.push(ACCESS_MAP.MANAGE_WHITE_LIST);
      }
      dispatch(
        Actions.updateUserPermission({
          ...userInfo,
          permission: nextPermission,
        }),
      );
    },
    [permission, userInfo, index],
  );

  return (
    <div className="white-list-permission-checkbox">
      <Checkbox
        checked={permission?.includes(ACCESS_MAP.MANAGE_WHITE_LIST)}
        onChange={onChange}
      />
    </div>
  );
};

const columns = [
  {
    dataIndex: 'name',
    key: 'name',
    title: 'Name',
    width: `${(150 / 1225) * 100}%`,
    className: 'name-col',
  },
  {
    dataIndex: 'guid',
    key: 'guid',
    title: 'GUID',
    width: `${(100 / 1225) * 100}%`,
  },
  {
    dataIndex: 'email',
    key: 'email',
    title: 'Email Address',
    width: `${(200 / 1225) * 100}%`,
  },
  {
    dataIndex: 'staff',
    key: 'staff',
    title: 'Staff Class',
    width: `${(135 / 1225) * 100}%`,
  },
  {
    dataIndex: 'jobFunc',
    key: 'jobFunc',
    title: 'PwC Competency',
    width: `${(180 / 1225) * 100}%`,
  },
  {
    dataIndex: 'permission',
    key: 'whiteList',
    title: 'White List Management',
    width: `${(150 / 1225) * 100}%`,
    render: (permission = [], record, index) => (
      <ServiceControlCheckBox
        permission={permission}
        userInfo={record}
        index={index}
      />
    ),
    align: 'center',
  },
  {
    dataIndex: 'permission',
    key: 'tool',
    title: 'Tool',
    width: `${(170 / 1225) * 100}%`,
    render: () => toolLabels,
  },
  {
    dataIndex: 'permission',
    key: 'control',
    title: 'Control',
    width: `${(60 / 1225) * 100}%`,
    render: (text, record, index) =>
      record?.nonUS ? null : (
        <PermissionCheckGroup
          permission={text}
          userInfo={record}
          options={ADMIN_CHECK_GROUP_OPTIONS}
          index={index}
        />
      ),
  },
  {
    dataIndex: 'permission',
    key: 'access',
    title: 'Access',
    width: `${(60 / 1225) * 100}%`,
    render: (text, record, index) => (
      <PermissionCheckGroup
        permission={text}
        userInfo={record}
        options={ACCESS_CHECK_GROUP_OPTIONS}
        index={index}
      />
    ),
  },
  {
    dataIndex: 'action',
    key: 'action',
    render: (...e) => <DeleteBtn currentIndex={e[2]} />,
    align: 'center',
  },
];

const filterData = (dataSource, text) => {
  if (!text) {
    return dataSource;
  }

  const filterText = text.toLowerCase();
  return dataSource.filter(
    ({ guid, email, name, staff }) =>
      guid.toLowerCase().includes(filterText) ||
      name.toLowerCase().includes(filterText) ||
      email.toLowerCase().includes(filterText) ||
      staff.toLowerCase().includes(filterText),
  );
};

function WhiteListTable({ filter }) {
  const dispatch = useDispatch();

  const [isLoading, toggleLoading] = useState(false);
  const { listData, isFetching } = useSelector(
    ({ systemSettings: { whiteList = {} } }) => ({
      listData: whiteList.listData || [],
      isFetching: whiteList.isFetching,
    }),
  );

  const handleDelete = async (guid) => {
    toggleLoading(true);
    await dispatch(Actions.deleteWhiteListMember(guid));
    toggleLoading(false);
  };

  const memoedColumn = useMemo(() => {
    const result = columns;
    result[columns.length - 1].render = (_, record, index) => (
      <DeleteBtn
        inputProps={{
          onRowDelete() {
            handleDelete(record.guid);
          },
        }}
      />
    );

    return result;
  }, []);

  useEffect(() => {
    dispatch(Actions.getWhiteListMemberList());
  }, []);

  return (
    <Table
      loading={isFetching || isLoading}
      pagination={{
        position: ['bottomLeft'],
        simple: true,
        className: 'ticket-pagination',
      }}
      rowKey={(r) => r.guid + Date.now()}
      className="dd-table"
      columns={memoedColumn}
      scroll={{ y: 400 }}
      dataSource={filterData(listData, filter.text)}
    />
  );
}

export default memo(WhiteListTable);
